import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../app/store";
import { useAppDispatch } from "./useAppDispatch";
import { ScheduleItemDto } from "../features/calendar/calendar.types";
import { getDurationBetweenDates } from "../helpers/timeParsing";
import { updateTask } from "../features/tasks/taskThunk";
import { NoteScheduledStatus } from "../features/tasks/types";
import { getIsDuringDayInUTC, partition } from "../helpers/helpers";
import { getTaskPriorityValue, useFilteredTasks } from "./useFilteredTasks";

export function getScheduledTime(scheduleItems: ScheduleItemDto[]) {
  return scheduleItems.reduce((acc, item) => {
    return (
      acc +
      getDurationBetweenDates(item.scheduleStart, item.scheduleEnd).duration
    );
  }, 0);
}

export const scheduleItemIsForDay = (
  scheduleItem: ScheduleItemDto,
  date?: Date,
) => {
  if (
    getIsDuringDayInUTC(scheduleItem.scheduleStart, date) ||
    getIsDuringDayInUTC(scheduleItem.scheduleEnd, date)
  ) {
    return true;
  }
  return false;
};

/**
 * Slot of a finished task. finishedTaskIds covers tasks finished in this
 * session (their slots in the store still carry the noteId), archivedNoteId
 * the ones the backend already moved to the archive.
 */
export const isFinishedTaskSlot = (
  item: ScheduleItemDto,
  finishedTaskIds: string[] = [],
) =>
  !!item.archivedNoteId ||
  (!!item.noteId && finishedTaskIds.includes(item.noteId));

/**
 *
 * @returns
 */

// syncScheduledStatus: write the scheduling status of the open task back.
// Only one caller (TaskLayout) may enable it, every instance would send its own update
export const useSchedule = ({
  syncScheduledStatus = false,
}: { syncScheduledStatus?: boolean } = {}) => {
  const dispatch = useAppDispatch();
  const { task, tasks } = useSelector((state: RootState) => state.tasks);
  const { sortedVisibleTasks } = useFilteredTasks();
  const { schedule, externalCalendarEntries, calendar } = useSelector(
    (state: RootState) => state.calendar,
  );

  // 1. Categorize calendar and schedule elements
  const {
    calendarElements,
    routineElements,
    calendarAllDayElements,
    calendarDeadlineElements,
    externalDefault,
    externalAllDay,
    taskScheduleElements,
    eventScheduleElements, // neu
  } = useMemo(() => {
    const calendarElements =
      calendar?.filter((e) => e.type === "default") || [];
    const routineElements = calendar?.filter((e) => e.type === "routine") || [];
    const calendarAllDayElements =
      calendar?.filter((e) => e.type === "allDay") || [];
    const calendarDeadlineElements =
      calendar?.filter((e) => e.type === "deadline") || [];
    const externalDefault =
      externalCalendarEntries?.filter((e) => e.type === "default") || [];
    const externalAllDay =
      externalCalendarEntries?.filter((e) => e.type === "allDay") || [];
    const taskScheduleElements =
      schedule?.filter((e) => e.type === "taskSchedule") || [];

    // Kombinierter Pool aller "echten" (nicht ganztägigen) Events für scheduledEventTimeForToday
    const eventScheduleElements = [
      ...calendarElements,
      ...routineElements,
      ...externalDefault,
    ];

    return {
      calendarElements,
      routineElements,
      calendarAllDayElements,
      calendarDeadlineElements,
      externalDefault,
      externalAllDay,
      taskScheduleElements,
      eventScheduleElements,
    };
  }, [calendar, schedule, externalCalendarEntries]);

  // 2. Task-specific schedule and calendar elements
  const taskScheduleElementsForTask = useMemo(() => {
    return task?.id
      ? taskScheduleElements
          .filter((e) => e.noteId === task.id)
          .sort(
            (a, b) =>
              new Date(a.scheduleEnd).getTime() -
              new Date(b.scheduleEnd).getTime(),
          )
      : [];
  }, [task?.id, taskScheduleElements, schedule]);

  const taskCalendarElements = useMemo(() => {
    return calendarElements.filter((e) => e.noteId === task?.id);
  }, [calendarElements, task?.id]);

  // 3. Compute scheduled time
  const scheduledTime = useMemo(() => {
    const time = getScheduledTime(taskScheduleElementsForTask) / 1000;
    const calendarTime = getScheduledTime(taskCalendarElements) / 1000;
    return time + calendarTime;
  }, [taskScheduleElementsForTask, taskCalendarElements]);

  // 4. Scheduling progress and status effect
  const estimatedWorkload = task?.timeEffort || 0;

  const schedulingProgress = useMemo(() => {
    if (!estimatedWorkload) return scheduledTime > 0 ? 100 : 0;
    return Math.min((scheduledTime / estimatedWorkload) * 100, 100);
  }, [scheduledTime, estimatedWorkload]);

  /*
  useEffect(() => {
    dispatch(setScheduledTime(scheduledTime));
  }, []);
  */

  useEffect(() => {
    if (!syncScheduledStatus || !task) return;
    let nextStatus: NoteScheduledStatus = "OPEN";
    if (schedulingProgress > 0 && schedulingProgress < 100)
      nextStatus = "PLANNING";
    else if (schedulingProgress >= 100) nextStatus = "PLANNED";

    if (task.scheduledStatus !== nextStatus) {
      dispatch(updateTask({ id: task.id, scheduledStatus: nextStatus }));
    }
  }, [syncScheduledStatus, schedulingProgress, task, dispatch]);

  // 5. Build scheduled tasks list with scheduledTime
  const scheduledTasks = useMemo(() => {
    if (!Array.isArray(tasks) || !Array.isArray(schedule)) return [];
    const taskMap = new Map<string, ScheduleItemDto[]>();
    for (const item of schedule) {
      if (item.noteId) {
        if (!taskMap.has(item.noteId)) taskMap.set(item.noteId, []);
        taskMap.get(item.noteId)?.push(item);
      }
    }
    const scheduled = tasks
      .filter((task) => taskMap.has(task.id))
      .map((task) => {
        const taskSchedule = taskMap.get(task.id)!;
        const scheduledTime = getScheduledTime(taskSchedule);
        return { ...task, schedule: taskSchedule, scheduledTime };
      })
      .sort((a, b) => {
        const aStart = new Date(a.schedule[0]?.scheduleStart || 0).getTime();
        const bStart = new Date(b.schedule[0]?.scheduleStart || 0).getTime();
        return aStart - bStart;
      });

    return scheduled;
  }, [tasks, schedule]);

  const tasksWithSchedulingStatus = useMemo(() => {
    return scheduledTasks.map((task) => {
      const futureSchedule =
        task.schedule?.filter((s) => new Date(s.scheduleStart) > new Date())
          .length || 0;
      return { ...task, scheduleInfo: { futureSchedule } };
    });
  }, [scheduledTasks]);

  /**
   * Returns future and past scheduled tasks.
   */
  const { futureScheduleTasks, pastScheduleTasks } = useMemo(() => {
    const [futureScheduleTasks, pastScheduleTasks] = partition(
      tasksWithSchedulingStatus,
      (task) => task.scheduleInfo.futureSchedule > 0,
    );
    return { futureScheduleTasks, pastScheduleTasks };
  }, [tasks]);

  const getTaskScheduleElements = useCallback(
    (taskId: string) => taskScheduleElements.filter((e) => e.noteId === taskId),
    [taskScheduleElements],
  );

  const getTaskScheduleElementForDay = useCallback(
    (taskId: string, day: Date) => {
      const forDay = getTaskScheduleElements(taskId).filter((sch) =>
        scheduleItemIsForDay(sch, day),
      );
      // A task can have several slots on one day (e.g. finished, then planned
      // again). Prefer the open one - that's the slot the user acts on. With
      // the first one, "finish" hit an already finished slot and nothing
      // changed while the open slot kept the task on today's plan.
      return forDay.find((sch) => sch.status === "OPEN") ?? forDay[0];
    },
    [getTaskScheduleElements],
  );

  /**
   * @param days - Array of Date objects representing the days to get the schedule for.
   * @param elements - Array of ScheduleItemDto objects to filter.
   */
  const getTaskScheduleForDays = useCallback(
    (days: Date[]) =>
      days.map((day) =>
        taskScheduleElements.filter((el) => scheduleItemIsForDay(el, day)),
      ),
    [taskScheduleElements],
  );

  const getEventScheduleForDay = useCallback(
    (day: Date) =>
      eventScheduleElements.filter((el) => scheduleItemIsForDay(el, day)),
    [eventScheduleElements],
  );

  /**
   * @param day - Date object representing the day to get the schedule for.
   * @param elements - Array of ScheduleItemDto objects to filter.
   */
  const getTaskScheduleForDay = useCallback(
    (day: Date) =>
      taskScheduleElements.filter((el) => scheduleItemIsForDay(el, day)),
    [taskScheduleElements],
  );

  const getScheduledTimeForArray = useCallback(
    (scheduleGroup: ScheduleItemDto[][]) =>
      scheduleGroup.map((day) => getScheduledTime(day) / 1000),
    [],
  );

  const eventsInTimeFrame = ({
    startDate = new Date(new Date().setHours(0, 0, 0, 0)),
    endDate = new Date(new Date().setHours(23, 59, 59, 999)),
  }) => {
    return calendarElements.filter((el) => {
      const start = new Date(el.scheduleStart);
      return start >= startDate && start <= endDate;
    });
  };

  const tasksScheduledToday = useCallback(
    (date: Date = new Date(), onlyShowOpen: boolean = false) => {
      const todayNoteIds = schedule
        ?.filter((sch) => scheduleItemIsForDay(sch, date))
        .filter((a) => {
          if (!onlyShowOpen) {
            return a;
          } else {
            return a.status === "OPEN";
          }
        })
        .map((sch) => sch.noteId);
      return scheduledTasks
        .filter((task) => todayNoteIds.includes(task.id))
        .sort((a, b) => getTaskPriorityValue(b) - getTaskPriorityValue(a));
    },
    [schedule, scheduledTasks, calendar],
  );

  const scheduledEventTimeForToday = useCallback(
    ({ day }: { day: Date }) => {
      const events = getEventScheduleForDay(day);

      const scheduledTime =
        getScheduledTimeForArray([events]).reduce((acc, b) => acc + b, 0) / 60;

      return {
        events,
        scheduledTime,
      };
    },
    [getEventScheduleForDay, getScheduledTimeForArray],
  );

  const tasksNotScheduledToday = useCallback(
    (date: Date = new Date()) => {
      const todayNoteIds = schedule
        ?.filter((sch) => scheduleItemIsForDay(sch, date))
        .map((sch) => sch.noteId);
      return sortedVisibleTasks.filter(
        (task) => !todayNoteIds.includes(task.id),
      );
    },
    [schedule, sortedVisibleTasks],
  );

  const scheduledTimeForToday = useCallback(
    ({ day }: { day: Date }) => {
      const tasks = tasksScheduledToday(day);
      const schedule = tasks.map((a) => a.schedule);
      const taskSchedule = getTaskScheduleForDay(day);

      const scheduledTime =
        getScheduledTimeForArray([taskSchedule]).reduce(
          (acc, b) => acc + b,
          0,
        ) / 60;
      return {
        tasks,
        schedule,
        scheduledTime,
      };
    },
    [schedule],
  );

  return {
    schedulingProgress,
    estimatedWorkload,
    taskScheduleElements: taskScheduleElementsForTask,
    scheduledTime,
    calendarElements,
    routineElements,
    calendarAllDayElements,
    calendarDeadlineElements,
    externalCalendarElements: externalDefault,
    externalcalendarAllDayElements: externalAllDay,
    taskCalendarElements,
    tasksScheduleElements: taskScheduleElements,
    scheduledTimeForToday,
    eventsInTimeFrame,
    tasksScheduledToday,
    tasksNotScheduledToday,
    scheduledEventTimeForToday,

    scheduledTasks,
    tasksWithSchedulingStatus,
    futureScheduleTasks,
    pastScheduleTasks,

    getTaskScheduleElements,
    getTaskScheduleElementForDay,
    getTaskScheduleForDays,
    getTaskScheduleForDay,
    getScheduledTimeForArray,
  };
};
